import type { Positions } from "./astro";

// ─────────────────────────────────────────────────────────────
// Human Design engine — published mandala + circuitry rules.
//
// Wheel: 64 gates of 5.625°, 6 lines of 0.9375° each. The wheel
// is anchored with Gate 41 starting at 02°00' Aquarius (=302.0°
// ecliptic longitude); sanity check: the vernal point (0° Aries)
// falls inside Gate 25 — matches the canonical mandala.
// ─────────────────────────────────────────────────────────────

/** Gate order around the zodiac starting at 302.0° (Gate 41). */
const WHEEL = [
  41, 19, 13, 49, 30, 55, 37, 63, 22, 36, 25, 17, 21, 51, 42, 3,
  27, 24, 2, 23, 8, 20, 16, 35, 45, 12, 15, 52, 39, 53, 62, 56,
  31, 33, 7, 4, 29, 59, 40, 64, 47, 6, 46, 18, 48, 57, 32, 50,
  28, 44, 1, 43, 14, 34, 9, 5, 26, 11, 10, 58, 38, 54, 61, 60,
];
const WHEEL_START = 302.0; // Gate 41 begins here
const GATE_SPAN = 360 / 64; // 5.625°
const LINE_SPAN = GATE_SPAN / 6; // 0.9375°

export interface Activation {
  point: string;
  gate: number;
  line: number; // 1..6
}

export function gateOf(lon: number): { gate: number; line: number } {
  const rel = (((lon - WHEEL_START) % 360) + 360) % 360;
  const idx = Math.floor(rel / GATE_SPAN);
  const line = Math.floor((rel % GATE_SPAN) / LINE_SPAN) + 1;
  // `rel` is normalised to [0,360), so idx is always inside WHEEL.
  return { gate: WHEEL[idx]!, line };
}

// ── Centers & channels ───────────────────────────────────────
export type Center =
  | "Head" | "Ajna" | "Throat" | "G" | "Heart"
  | "Spleen" | "SolarPlexus" | "Sacral" | "Root";

const MOTORS: Center[] = ["Sacral", "SolarPlexus", "Heart", "Root"];

/** The 36 channels: [gateA, gateB, centerA, centerB] */
const CHANNELS: Array<[number, number, Center, Center]> = [
  [64, 47, "Head", "Ajna"], [61, 24, "Head", "Ajna"], [63, 4, "Head", "Ajna"],
  [17, 62, "Ajna", "Throat"], [43, 23, "Ajna", "Throat"], [11, 56, "Ajna", "Throat"],
  [31, 7, "Throat", "G"], [8, 1, "Throat", "G"], [33, 13, "Throat", "G"],
  [20, 34, "Throat", "Sacral"], [16, 48, "Throat", "Spleen"], [20, 57, "Throat", "Spleen"],
  [35, 36, "Throat", "SolarPlexus"], [12, 22, "Throat", "SolarPlexus"], [21, 45, "Heart", "Throat"],
  [5, 15, "Sacral", "G"], [14, 2, "Sacral", "G"], [29, 46, "Sacral", "G"],
  [10, 20, "G", "Throat"], [10, 34, "G", "Sacral"], [10, 57, "G", "Spleen"],
  [25, 51, "G", "Heart"],
  [53, 42, "Root", "Sacral"], [60, 3, "Root", "Sacral"], [52, 9, "Root", "Sacral"],
  [27, 50, "Sacral", "Spleen"], [34, 57, "Sacral", "Spleen"], [59, 6, "Sacral", "SolarPlexus"],
  [18, 58, "Spleen", "Root"], [28, 38, "Spleen", "Root"], [32, 54, "Spleen", "Root"],
  [30, 41, "SolarPlexus", "Root"], [55, 39, "SolarPlexus", "Root"], [49, 19, "SolarPlexus", "Root"],
  [26, 44, "Heart", "Spleen"], [40, 37, "Heart", "SolarPlexus"],
];

export type HDType =
  | "Generator" | "Manifesting Generator" | "Projector" | "Manifestor" | "Reflector";

export interface HDResult {
  type: HDType;
  /** app-internal key matching personality.ts HUMAN_DESIGN */
  typeKey: "generator" | "mgen" | "projector" | "manifestor" | "reflector";
  authority: string;
  profile: string; // e.g. "3/5"
  definedCenters: Center[];
  definedChannels: string[]; // "34-20 (Sacral–Throat)"
  personality: Activation[];
  design: Activation[];
}

export function computeHD(personality: Positions, design: Positions): HDResult {
  const acts = (pos: Positions, tag: string): Activation[] =>
    Object.entries(pos).map(([point, lon]) => {
      const { gate, line } = gateOf(lon);
      return { point: `${tag}.${point}`, gate, line };
    });

  const pActs = acts(personality, "p");
  const dActs = acts(design, "d");
  const gates = new Set<number>([...pActs, ...dActs].map((a) => a.gate));

  // defined channels → defined centers graph
  const definedChannels: Array<[Center, Center, string]> = [];
  for (const [a, b, ca, cb] of CHANNELS) {
    if (gates.has(a) && gates.has(b)) {
      definedChannels.push([ca, cb, `${a}-${b} (${ca}–${cb})`]);
    }
  }
  const defined = new Set<Center>();
  const adj = new Map<Center, Set<Center>>();
  for (const [ca, cb] of definedChannels) {
    defined.add(ca);
    defined.add(cb);
    if (!adj.has(ca)) adj.set(ca, new Set());
    if (!adj.has(cb)) adj.set(cb, new Set());
    adj.get(ca)!.add(cb);
    adj.get(cb)!.add(ca);
  }

  // connectivity: is any motor connected to the Throat through defined channels?
  const connectedToThroat = (start: Center): boolean => {
    if (!defined.has(start) || !defined.has("Throat")) return false;
    const seen = new Set<Center>([start]);
    const stack = [start];
    while (stack.length) {
      const c = stack.pop()!;
      if (c === "Throat") return true;
      for (const n of adj.get(c) ?? []) {
        if (!seen.has(n)) {
          seen.add(n);
          stack.push(n);
        }
      }
    }
    return false;
  };
  const motorToThroat = MOTORS.some((m) => connectedToThroat(m));
  const sacral = defined.has("Sacral");

  let type: HDType;
  if (defined.size === 0) type = "Reflector";
  else if (sacral) type = motorToThroat ? "Manifesting Generator" : "Generator";
  else if (motorToThroat) type = "Manifestor";
  else type = "Projector";

  const typeKey =
    type === "Generator" ? "generator"
    : type === "Manifesting Generator" ? "mgen"
    : type === "Projector" ? "projector"
    : type === "Manifestor" ? "manifestor"
    : "reflector";

  // authority hierarchy
  let authority: string;
  if (type === "Reflector") authority = "Lunar (a full cycle before big decisions)";
  else if (defined.has("SolarPlexus")) authority = "Emotional — clarity comes over time, never in the meeting";
  else if (defined.has("Sacral")) authority = "Sacral — the gut response is the answer";
  else if (defined.has("Spleen")) authority = "Splenic — the first quiet instinct, in the moment";
  else if (defined.has("Heart") && connectedToThroat("Heart")) authority = "Ego — what do I have the will (and stake) for?";
  else if (defined.has("G") && connectedToThroat("G")) authority = "Self-projected — hear yourself talk it out";
  else authority = "Mental — sounding board: talk it through, decide outside the room";

  const pSun = pActs.find((a) => a.point === "p.sun")!;
  const dSun = dActs.find((a) => a.point === "d.sun")!;
  const profile = `${pSun.line}/${dSun.line}`;

  return {
    type,
    typeKey,
    authority,
    profile,
    definedCenters: [...defined],
    definedChannels: definedChannels.map(([, , label]) => label),
    personality: pActs,
    design: dActs,
  };
}
